/**
 * System status, shared across the shell and the cockpit.
 *
 * The counts here drive the rail badge and the top bar, which every screen
 * shows, so they live in one store rather than being fetched independently by
 * each consumer. Polling per component would turn a multi-panel view into
 * several request loops for the same numbers.
 *
 * Two sources: the realtime snapshot on `/ws/events` carries the current active
 * session list and the session counters, and `/health` reports the dependency
 * status (a request, because it must be answerable before any event arrives).
 * The counters move between snapshots as `session.*` events arrive, corrected by
 * the next snapshot which counts live records rather than trusting increments.
 */

import { create } from 'zustand'
import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { DomainEvent, ISOTime, SystemHealth } from '@/types/domain'
import type { ConnectionState } from '@/realtime/events'
import { EventStream } from '@/realtime/events'
import { api, reportUnauthorized } from '@/api/client'

/**
 * The realtime snapshot document, exactly as api.EventSnapshot marshals it.
 *
 * The collections are typed loosely because this store consumes only their
 * counts; the cockpit screen fetches the full session list from `/sessions`.
 * The `sessions` counter block is what the pivot's overview seam adds; until a
 * server carries it, the active count falls back to the length of the active
 * list, which the snapshot has always sent.
 */
interface SessionCounters {
  active: number
  waitingInput: number
}

interface EventSnapshot {
  generatedAt: ISOTime
  accounts: unknown[]
  hosts: unknown[]
  activeSessions: unknown[]
  sessions?: SessionCounters
}

/** Which cached queries an event invalidates.
 *
 *  The console reads most screens through React Query and, until now, nothing
 *  connected the two systems: a socket event updated a counter in this store and
 *  no cached list ever heard about it. So an account probed in one tab, or a
 *  host that went offline, stayed on screen as it was until the operator
 *  themselves performed a mutation or reloaded the page.
 *
 *  Prefix-matched, so `account.auth.updated` and `account.created` both land on
 *  the account lists without enumerating every type the server may add. */
const EVENT_QUERY_KEYS: Array<{ prefix: string; keys: string[][] }> = [
  { prefix: 'session.', keys: [['sessions'], ['overview']] },
  { prefix: 'mission.', keys: [['missions'], ['overview']] },
  { prefix: 'task.', keys: [['tasks'], ['missions'], ['overview']] },
  { prefix: 'conductor.', keys: [['conductor-runs'], ['tasks'], ['sessions'], ['overview']] },
  { prefix: 'account.', keys: [['accounts'], ['accounts-matrix'], ['overview']] },
  { prefix: 'host.', keys: [['hosts'], ['overview']] },
  { prefix: 'project.', keys: [['projects'], ['overview']] },
]

const MAX_RECENT_EVENTS = 200

interface SystemStatusState {
  connection: ConnectionState
  health: SystemHealth | null
  /**
   * The build this tab loaded with, and whether the server has moved past it.
   *
   * A single-page console never re-fetches its own index.html: it routes in the
   * browser, so a tab left open runs the bundle it was loaded with for as long
   * as it is open, however many times the control plane is redeployed
   * underneath it. That is not theoretical — a tab thirty-three hours behind was
   * being read as the current interface, and its screens genuinely no longer
   * exist. Worse than the confusion: an old client keeps calling a new API.
   *
   * No build-time stamping is needed to see it. `/health` already reports the
   * server's commit and this store already polls it every thirty seconds, so
   * the first version this tab saw *is* the build it is running. When a later
   * poll disagrees, the server was redeployed after this page loaded.
   */
  build: string | null
  serverBuild: string | null
  /** Sessions currently live on any host. Drives the cockpit nav badge. */
  activeSessions: number
  /** Most recent events, newest first, bounded. Read by the inspector's
   *  per-session timeline, which is the only surface with a use for them: the
   *  pool-wide feed that used to sit in the cockpit rail showed bare event type
   *  strings with no subject, and was removed rather than dressed up. */
  recentEvents: DomainEvent[]

  applySnapshot: (data: unknown) => void
  applyEvent: (event: DomainEvent) => void
  setConnection: (state: ConnectionState) => void
  setHealth: (health: SystemHealth) => void
}

export const useSystemStatusStore = create<SystemStatusState>((set) => ({
  connection: 'closed',
  health: null,
  build: null,
  serverBuild: null,
  activeSessions: 0,
  recentEvents: [],

  applySnapshot: (raw) => {
    const data: Partial<EventSnapshot> =
      raw !== null && typeof raw === 'object' ? (raw as Partial<EventSnapshot>) : {}
    set({
      // Prefer the explicit counter block; fall back to the active list length,
      // which every snapshot has carried, so a server that predates the counters
      // still lights the badge honestly rather than showing a confident zero.
      activeSessions: numberFrom(data.sessions?.active) || arrayLen(data.activeSessions),
      // recentEvents is left alone rather than reset: the snapshot carries no
      // event history, so writing it here would throw away the live feed on every
      // reconnect — exactly when an operator is most likely watching it.
    })
  },

  applyEvent: (event) =>
    set((s) => {
      const next: Partial<SystemStatusState> = {
        recentEvents: [event, ...s.recentEvents].slice(0, MAX_RECENT_EVENTS),
      }
      // The badge moves on events rather than waiting for the next snapshot.
      // Drift is corrected by the next snapshot, which counts live records.
      switch (event.type) {
        case 'session.created':
        case 'session.started':
          next.activeSessions = s.activeSessions + 1
          break
        case 'session.completed':
        case 'session.failed':
        case 'session.terminated':
          next.activeSessions = Math.max(0, s.activeSessions - 1)
          break
      }
      return next
    }),

  setConnection: (connection) => set({ connection }),
  // The first non-empty version wins and is never rewritten: it is what this
  // tab is running. An empty one is not a build — an older server, or a
  // degraded response — and must not be latched as one.
  setHealth: (health) =>
    set((prev) => ({
      health,
      build: prev.build ?? (health.version || null),
      serverBuild: health.version || prev.serverBuild,
    })),
}))

function numberFrom(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

function arrayLen(v: unknown): number {
  return Array.isArray(v) ? v.length : 0
}

/** Reads the shared status. Safe to call from any component. */
export function useSystemStatus() {
  return useSystemStatusStore()
}

/**
 * Opens the single application-wide event stream.
 *
 * Mounted once, by the root. Calling it twice would open two sockets and apply
 * every event twice, so it is deliberately not a general-purpose hook.
 */
export function useEventStreamConnection() {
  const { applySnapshot, applyEvent, setConnection, setHealth } = useSystemStatusStore()
  const queryClient = useQueryClient()

  useEffect(() => {
    let cancelled = false

    // A `state` frame *is* the `/system/overview` document — the server builds
    // both from one function — so it is written straight into that query's cache
    // rather than kept somewhere the screens would have to learn about. Every
    // consumer of useOverview() becomes socket-driven without changing a line.
    //
    // Only the state frame. The connect-time `snapshot` is a different document
    // (api.EventSnapshot: it carries the active-session list and no risks) and
    // writing it here overwrote the overview with a version missing half its
    // fields. Every risk disappeared from the screen ten seconds after a page
    // loaded, and polling is suspended while the socket is up, so nothing
    // brought them back.
    const applyPoolState = (raw: unknown) => {
      if (raw === null || typeof raw !== 'object') return
      queryClient.setQueryData(['overview'], raw)
    }

    // An event says something changed; it does not say what the new list looks
    // like. Invalidating is therefore right and refetching by hand would not be:
    // React Query refetches only what is actually mounted, so a screen nobody is
    // looking at costs nothing.
    const invalidateFor = (type: string) => {
      for (const rule of EVENT_QUERY_KEYS) {
        if (!type.startsWith(rule.prefix)) continue
        for (const key of rule.keys) {
          void queryClient.invalidateQueries({ queryKey: key })
        }
      }
    }

    // Health is a request rather than an event because it must be available
    // before the first event arrives — the operator needs to know a dependency
    // is down even when nothing is happening.
    const loadHealth = async () => {
      try {
        const health = await api.get<SystemHealth>('/health')
        if (!cancelled) setHealth(health)
      } catch {
        // A failed health request is itself reported through the connection
        // indicator; there is nothing useful to add here.
      }
    }

    const stream = new EventStream({
      onSnapshot: (data) => {
        applySnapshot(data)
      },
      onState: (data) => {
        applySnapshot(data)
        applyPoolState(data)
      },
      onEvent: (event) => {
        applyEvent(event)
        invalidateFor(event.type)
      },
      onStateChange: (state) => setConnection(state),
      // The socket is where an ended session is noticed first: the hub re-checks
      // the credential on its own timer, while a console parked on one screen
      // may issue no request for minutes. Routed into the same handler a 401
      // uses, so both arrive at the sign-in screen by one path.
      onUnauthorised: () => reportUnauthorized(),
    })
    stream.connect({})

    void loadHealth()
    const timer = setInterval(() => {
      void loadHealth()
    }, 30_000)

    // Coming back to the tab, or back onto the network, are the two moments the
    // socket is most likely to have died without saying so — and a hidden tab's
    // timers are clamped to about one firing a minute, so the health poll above
    // is no substitute. Ask the stream to check itself, and refresh the health
    // panel at the same time rather than showing a half-minute-old verdict to
    // somebody who has just looked back at it.
    const revive = () => {
      stream.probe()
      void loadHealth()
    }
    // Only when it becomes visible: the event also fires on the way out, and
    // probing a tab nobody is looking at buys nothing.
    const onVisible = () => {
      if (document.visibilityState === 'visible') revive()
    }
    document.addEventListener('visibilitychange', onVisible)
    // Unconditional, hidden or not — a console left in a background tab is
    // there to raise an alarm, and it cannot do that from a dead socket.
    window.addEventListener('online', revive)
    // And the case a phone makes routine. A backgrounded page on iOS is frozen
    // and then restored from the back/forward cache, and on the way in every
    // socket it held was closed with no close event delivered here — so the
    // stream believes it is open, the watchdog's timers never ran while frozen,
    // and the console comes back showing whatever the fleet was doing before the
    // screen locked. `pageshow` is the only event that fires on that path; the
    // terminal pane has listened for it since it was written and this one did
    // not, so a phone unlocked to a live-looking dashboard and a live terminal
    // beneath it.
    window.addEventListener('pageshow', revive)

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('online', revive)
      window.removeEventListener('pageshow', revive)
      clearInterval(timer)
      stream.close()
    }
  }, [])
}
